import React, { useState, useEffect, useCallback } from 'react';
import { partnersAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './Partners.css';

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, total_requests: 0 });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ partner_name: '', email: '', environment: 'test', fee_usd: '15' });
  const [creating, setCreating] = useState(false);

  // A freshly-minted key (from create / regenerate / approve) — shown once.
  const [keyModal, setKeyModal] = useState(null);

  // Wallet drawer: { partner, loading, balance_cents, currency, txns }
  const [wallet, setWallet] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ amount_usd: '', kind: 'credit', note: '' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [partnersRes, statsRes, reqRes] = await Promise.all([
        partnersAPI.getAll(),
        partnersAPI.getStats(),
        partnersAPI.getRequests()
      ]);
      setPartners(partnersRes.data.data);
      setStats(statsRes.data.data);
      setRequests(reqRes.data.data || []);
    } catch (err) {
      console.error('Failed to fetch partners:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.partner_name.trim() || !createForm.email.trim()) return;
    try {
      setCreating(true);
      const fee_cents = Math.max(0, Math.round(parseFloat(createForm.fee_usd || '0') * 100));
      const res = await partnersAPI.create({
        partner_name: createForm.partner_name,
        email: createForm.email,
        environment: createForm.environment,
        wholesale_fee_cents: fee_cents
      });
      setShowCreate(false);
      setKeyModal(res.data.data.api_key);
      setToast({ type: 'success', message: 'Partner created! Copy the API key now.' });
      fetchData();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.message || 'Failed to create partner' });
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (partner) => {
    try {
      await partnersAPI.update(partner.id, { is_active: !partner.is_active });
      setToast({ type: 'success', message: `Partner ${partner.is_active ? 'deactivated' : 'activated'}` });
      fetchData();
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to update partner' });
    }
  };

  const handleRevoke = async (partner) => {
    if (!window.confirm(`Revoke API key for "${partner.partner_name}"? This cannot be undone.`)) return;
    try {
      await partnersAPI.revoke(partner.id);
      setToast({ type: 'success', message: 'API key revoked' });
      fetchData();
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to revoke key' });
    }
  };

  const handleRegenerate = async (partner) => {
    if (!window.confirm(`Generate a new API key for "${partner.partner_name}"? The old key will stop working.`)) return;
    try {
      const res = await partnersAPI.regenerateKey(partner.id);
      setKeyModal(res.data.data.api_key);
      setToast({ type: 'success', message: 'New API key generated! Copy it now.' });
      fetchData();
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to regenerate key' });
    }
  };

  const handleEditRate = async (partner) => {
    const cur = ((partner.wholesale_fee_cents || 0) / 100).toFixed(2);
    const v = window.prompt(`Wholesale fee per traveller (USD) for "${partner.partner_name}":`, cur);
    if (v == null) return;
    const cents = Math.round(parseFloat(v) * 100);
    if (!Number.isFinite(cents) || cents < 0) { setToast({ type: 'error', message: 'Enter a valid amount' }); return; }
    try {
      await partnersAPI.update(partner.id, { wholesale_fee_cents: cents });
      setToast({ type: 'success', message: 'Rate updated' });
      fetchData();
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to update rate' });
    }
  };

  const openWallet = async (partner) => {
    setWallet({ partner, loading: true, txns: [] });
    setAdjustForm({ amount_usd: '', kind: 'credit', note: '' });
    try {
      const res = await partnersAPI.getLedger(partner.id);
      setWallet({ partner, loading: false, ...res.data.data });
    } catch (err) {
      setWallet({ partner, loading: false, txns: [], balance_cents: 0, currency: 'USD' });
      setToast({ type: 'error', message: 'Failed to load wallet' });
    }
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    if (!wallet?.partner) return;
    const cents = Math.round(parseFloat(adjustForm.amount_usd || '0') * 100);
    if (!Number.isFinite(cents) || cents <= 0) { setToast({ type: 'error', message: 'Enter a positive amount' }); return; }
    try {
      await partnersAPI.adjustWallet(wallet.partner.id, { amount_cents: cents, kind: adjustForm.kind, note: adjustForm.note });
      setToast({ type: 'success', message: 'Wallet adjusted' });
      await openWallet(wallet.partner);
      fetchData();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.message || 'Failed to adjust wallet' });
    }
  };

  const handleApprove = async (reqRow) => {
    if (!window.confirm(`Approve "${reqRow.org_name}" and issue an API key?`)) return;
    try {
      const res = await partnersAPI.approveRequest(reqRow.id, {});
      setKeyModal(res.data.data.api_key);
      setToast({ type: 'success', message: 'Approved — copy the API key now.' });
      fetchData();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.message || 'Failed to approve' });
    }
  };

  const handleReject = async (reqRow) => {
    if (!window.confirm(`Reject the request from "${reqRow.org_name}"?`)) return;
    try {
      await partnersAPI.rejectRequest(reqRow.id);
      setToast({ type: 'success', message: 'Request rejected' });
      fetchData();
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to reject' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setToast({ type: 'success', message: 'Copied to clipboard!' });
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const formatNumber = (n) => (n || 0).toLocaleString();
  const usd = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

  const pendingRequests = requests.filter(r => r.status === 'pending');

  return (
    <div className="page-layout">
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div>
            <h1 className="page-title">API Partners</h1>
            <p className="page-subtitle">MCP integrators, prepaid wallets & access requests</p>
          </div>
          <button className="btn-primary" onClick={() => { setShowCreate(true); setCreateForm({ partner_name: '', email: '', environment: 'test', fee_usd: '15' }); }}>
            + New Partner
          </button>
        </div>

        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card"><div className="stat-value">{formatNumber(stats.total)}</div><div className="stat-label">Total Partners</div></div>
          <div className="stat-card"><div className="stat-value">{formatNumber(stats.active)}</div><div className="stat-label">Active</div></div>
          <div className="stat-card"><div className="stat-value">{formatNumber(stats.inactive)}</div><div className="stat-label">Inactive</div></div>
          <div className="stat-card"><div className="stat-value">{formatNumber(pendingRequests.length)}</div><div className="stat-label">Pending Requests</div></div>
        </div>

        {/* Access requests — always visible so the queue is obvious. */}
        <div className="table-container" style={{ marginBottom: 24 }}>
          <div style={{ padding: '12px 16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            API Key Requests
            {pendingRequests.length > 0 && (
              <span className="status-badge active" style={{ fontSize: 12 }}>{pendingRequests.length} pending</span>
            )}
          </div>
          {requests.length === 0 ? (
            <div className="empty-state">No API key requests yet. Requests from the developer docs appear here to approve.</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Organisation</th><th>Contact</th><th>Note</th><th>Status</th><th>Requested</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id}>
                    <td><div className="partner-name">{r.org_name}</div></td>
                    <td>{r.contact_email}</td>
                    <td style={{ maxWidth: 280, whiteSpace: 'normal' }}>{r.note || '-'}</td>
                    <td>
                      <span className={`status-badge ${r.status === 'pending' ? 'active' : 'inactive'}`}>{r.status}</span>
                    </td>
                    <td>{formatDate(r.created_at)}</td>
                    <td>
                      {r.status === 'pending' ? (
                        <div className="action-buttons">
                          <button className="btn-sm btn-primary" onClick={() => handleApprove(r)}>Approve</button>
                          <button className="btn-sm btn-danger" onClick={() => handleReject(r)}>Reject</button>
                        </div>
                      ) : (
                        <span style={{ color: '#888', fontSize: 13 }}>{r.status === 'approved' ? `partner #${r.partner_id}` : '—'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Partners table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-state">Loading partners...</div>
          ) : partners.length === 0 ? (
            <div className="empty-state">No partners yet. Create one to get started.</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Key Prefix</th>
                  <th>Env</th>
                  <th>Rate/traveller</th>
                  <th>Wallet</th>
                  <th>Status</th>
                  <th>Requests</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div className="partner-name">{p.partner_name}</div>
                      <div className="partner-email">{p.email}</div>
                    </td>
                    <td><code>{p.api_key_prefix}...</code></td>
                    <td><span className={`env-badge env-${p.environment}`}>{p.environment}</span></td>
                    <td>
                      <button className="btn-sm btn-ghost" onClick={() => handleEditRate(p)} title="Edit rate">
                        {usd(p.wholesale_fee_cents)} ✎
                      </button>
                    </td>
                    <td>
                      <button className="btn-sm btn-ghost" onClick={() => openWallet(p)} title="View wallet & ledger">
                        <strong>{usd(p.wallet_balance_cents)}</strong>
                      </button>
                    </td>
                    <td>
                      <span className={`status-badge ${p.is_active && !p.revoked_at ? 'active' : 'inactive'}`}>
                        {p.revoked_at ? 'Revoked' : p.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{formatNumber(p.request_count)}</td>
                    <td>
                      <div className="action-buttons">
                        {!p.revoked_at && (
                          <>
                            <button className="btn-sm btn-ghost" onClick={() => handleToggleActive(p)}>
                              {p.is_active ? 'Pause' : 'Resume'}
                            </button>
                            <button className="btn-sm btn-ghost" onClick={() => handleRegenerate(p)}>Regen</button>
                          </>
                        )}
                        <button className="btn-sm btn-danger" onClick={() => handleRevoke(p)}>Revoke</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="modal-overlay" onClick={() => setShowCreate(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Create Partner</h2>
                <button className="modal-close" onClick={() => setShowCreate(false)}>&times;</button>
              </div>
              <form onSubmit={handleCreate} className="modal-body">
                <div className="form-group">
                  <label>Partner Name</label>
                  <input type="text" value={createForm.partner_name} onChange={e => setCreateForm({ ...createForm, partner_name: e.target.value })} placeholder="Company name" required />
                </div>
                <div className="form-group">
                  <label>Contact Email</label>
                  <input type="email" value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })} placeholder="dev@company.com" required />
                </div>
                <div className="form-group">
                  <label>Environment</label>
                  <select value={createForm.environment} onChange={e => setCreateForm({ ...createForm, environment: e.target.value })}>
                    <option value="test">Test</option>
                    <option value="live">Live</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Wholesale fee per traveller (USD)</label>
                  <input type="number" step="0.01" min="0" value={createForm.fee_usd} onChange={e => setCreateForm({ ...createForm, fee_usd: e.target.value })} placeholder="15.00" />
                </div>
                <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={creating}>
                  {creating ? 'Creating...' : 'Create Partner'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* API key reveal modal (create / regen / approve) */}
        {keyModal && (
          <div className="modal-overlay" onClick={() => setKeyModal(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>API Key</h2>
                <button className="modal-close" onClick={() => setKeyModal(null)}>&times;</button>
              </div>
              <div className="modal-body">
                <div className="api-key-display">
                  <p className="api-key-warning">Copy this key now. It will not be shown again.</p>
                  <div className="api-key-box">
                    <code>{keyModal}</code>
                    <button className="btn-copy" onClick={() => copyToClipboard(keyModal)}>Copy</button>
                  </div>
                </div>
                <button className="btn-primary" style={{ width: '100%', marginTop: 16 }} onClick={() => setKeyModal(null)}>Done</button>
              </div>
            </div>
          </div>
        )}

        {/* Wallet + ledger modal */}
        {wallet && (
          <div className="modal-overlay" onClick={() => setWallet(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
              <div className="modal-header">
                <h2>Wallet — {wallet.partner.partner_name}</h2>
                <button className="modal-close" onClick={() => setWallet(null)}>&times;</button>
              </div>
              <div className="modal-body">
                <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>
                  Balance: {usd(wallet.balance_cents)} <span style={{ fontSize: 13, fontWeight: 400, color: '#888' }}>{wallet.currency || 'USD'}</span>
                </div>

                <form onSubmit={handleAdjust} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Amount (USD)</label>
                    <input type="number" step="0.01" min="0" style={{ width: 110 }} value={adjustForm.amount_usd} onChange={e => setAdjustForm({ ...adjustForm, amount_usd: e.target.value })} placeholder="0.00" required />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Type</label>
                    <select value={adjustForm.kind} onChange={e => setAdjustForm({ ...adjustForm, kind: e.target.value })}>
                      <option value="credit">Credit (add)</option>
                      <option value="refund">Refund (add)</option>
                      <option value="debit">Debit (remove)</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 140 }}>
                    <label>Note</label>
                    <input type="text" value={adjustForm.note} onChange={e => setAdjustForm({ ...adjustForm, note: e.target.value })} placeholder="reason" />
                  </div>
                  <button type="submit" className="btn-primary">Apply</button>
                </form>

                <div className="table-container" style={{ maxHeight: 320, overflowY: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr><th>When</th><th>Kind</th><th>Amount</th><th>Balance</th><th>Ref</th><th>Note</th></tr>
                    </thead>
                    <tbody>
                      {wallet.loading ? (
                        <tr><td colSpan={6}>Loading…</td></tr>
                      ) : (wallet.txns || []).length === 0 ? (
                        <tr><td colSpan={6}>No transactions yet.</td></tr>
                      ) : wallet.txns.map(t => (
                        <tr key={t.id}>
                          <td>{formatDateTime(t.created_at)}</td>
                          <td>{t.kind}</td>
                          <td style={{ color: t.amount_cents < 0 ? '#c0392b' : '#1e8449' }}>
                            {t.amount_cents < 0 ? '-' : '+'}{usd(Math.abs(t.amount_cents))}
                          </td>
                          <td>{usd(t.balance_after_cents)}</td>
                          <td>{t.ref_application_id ? `app #${t.ref_application_id}` : (t.ref_stripe_session ? 'stripe' : '-')}</td>
                          <td style={{ maxWidth: 160, whiteSpace: 'normal' }}>{t.note || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div className={`toast toast-${toast.type}`}>{toast.message}</div>
        )}
      </main>
    </div>
  );
};

export default Partners;
